import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  Placement,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import React, { PropsWithChildren, ReactNode, useState } from 'react';
import { twMerge } from 'tailwind-merge';

const DottedUnderline: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <span
      className="inline-flex bg-[length:3px_1px] bg-bottom bg-repeat-x"
      style={{
        // color content-contentSecondary 50% opacity
        backgroundImage:
          'linear-gradient(to right, rgb(146 150 153 / 50%) 33%, rgba(255,255,255,0) 0%)',
      }}
    >
      {children}
    </span>
  );
};

interface TooltipProps {
  children: ReactNode;
  content: ReactNode;
  className?: string;
  placement?: Placement;
  dottedUnderline?: boolean;
  open?: boolean;
}
/**
 * Reusable Tooltip Component
 *
 * This component creates an accessible, flexible tooltip using Floating UI.
 * It handles positioning, interactions, and portal rendering automatically.
 *
 * @example
 * ```tsx
 * <Tooltip content="User info" placement="bottom">
 *   <button>Hover me</button>
 * </Tooltip>
 * ```
 *
 * @notes
 * - Uses Floating UI for positioning and interactions
 * - Renders tooltip content in a portal to avoid layout issues
 * - Supports custom placement
 * - Handles hover, focus, and dismiss interactions
 * - Provides proper ARIA attributes for accessibility
 */
export const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  className,
  placement = 'top',
  dottedUnderline = false,
  open: openProp,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Set up Floating UI
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    // Ensures the tooltip stays anchored to its reference element
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5), // Adds a small offset from the reference element
      flip({ fallbackAxisSideDirection: 'start' }), // Flips the tooltip if it would overflow the viewport
      shift(), // Shifts the tooltip along the cross axis if it overflows
    ],
  });
  const hover = useHover(context, { move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);
  // If content is null or undefined or an empty string, don't render the
  // tooltip. Other falsy values like `0` will be rendered.
  if (content === null || content === undefined || content === '') {
    return <>{children}</>;
  }
  return (
    <>
      <div
        className={twMerge('inline-flex cursor-help hover:brightness-[130%]', className)}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        {dottedUnderline ? <DottedUnderline>{children}</DottedUnderline> : children}
      </div>
      <FloatingPortal>
        {(openProp || isOpen) && (
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className="z-50 max-w-96 rounded bg-backgroundTertiary p-2 text-xs text-contentPrimary shadow-md"
            {...getFloatingProps()}
          >
            {content}
          </div>
        )}
      </FloatingPortal>
    </>
  );
};
